import type { Doc } from "building-types/doc";
import { docEntityTypeToChildTypes } from "building-types/doc/docEntityTypeToForeignKeyRelations";
import type { DocRefEligibleEntityType } from "building-types/entities/docRefEligibleEntityTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import {
  isBuiltInCipComponent,
  isBuiltInMasonryComponent,
  isBuiltInSheathingComponent,
  isBuiltInSlabFloorComponent,
} from "./defaults/assemblyComponentDefaults";
import { isBuiltInFrameFloorAssembly, isBuiltInSlabFloorAssembly } from "./defaults/defaultFloorAssembly";
import { docEntityGetRoomLayout } from "./docRoomLayouts";
import { isBuiltInFoundationAssembly } from "./elements/foundations/defaultFoundationAssemblies";
import { isBuiltInFoundationComponent } from "./elements/foundations/defaultFoundationComponents";
import { isBuiltInFramingComponent } from "./elements/framing/defaultFramingComponents";
import { isBuiltInHeaderAssembly } from "./elements/framing/defaultHeaderAssembly";
import { isSystemDefaultProfile } from "./elements/trim/defaultTrimProfiles";
import docEntityGetParent from "./entity/docEntityGetParent";
import { docEntityHandleToEntityRef } from "./entity/docEntityHandleToEntityRef";
import { docEntityOrParentIsLocked } from "./entity/docEntityLockable";
import type { DocEntityChildrenEntityType } from "./entity/docEntityTypeUtils";
import { docGetGroup } from "./entity/docGetGroup";
import { docEntityGetGroup } from "./entity/entityGroupTools";
import { isBuiltInLabel } from "./utils/builtInLabelUtils";

/**
 * Plan Set Templates are unique in that, regardless of whether or not they are doc ref'd, we want to allow for
 * the addition, deletion or modification of any related Plan Set Template Configs (exports).
 */
export const docRefEligibleEntityTypeToMutableChildTypes: {
  [K in DocRefEligibleEntityType]?: DocEntityChildrenEntityType<K>[];
} = {
  PLAN_SET_TEMPLATE: ["PLAN_SET_TEMPLATE_CONFIG"],
};

/**
 * There are certain circumstances in which a document referenced Plan Set Template, Content Template, or Content Template Instance
 * may have been detached from the doc ref, creating local copies of the given entity's dependency tree. In such cases, users are
 * able to add, update or delete entities within that dependency tree. In the case of entities being added locally to a previously
 * doc ref'd parent entity, if that entity is "re-attached" to the upstream doc ref, we must allow for those local entities to be
 * deleted.
 */
export const docRefEligibleEntityTypeToMutableDetachedChildTypes = {
  PLAN_SET_TEMPLATE: docEntityTypeToChildTypes.PLAN_SET_TEMPLATE,
  CONTENT_TEMPLATE: docEntityTypeToChildTypes.CONTENT_TEMPLATE,
  CONTENT_TEMPLATE_INSTANCE: docEntityTypeToChildTypes.CONTENT_TEMPLATE_INSTANCE,
};

export function docEntityIsMutableDetachedChild(doc: Doc, handle: DocEntityHandle): boolean {
  // If the entity is an entity ref, then it is not considered a detached child
  if (docEntityHandleToEntityRef(doc, handle)) {
    return false;
  }

  const parent = docEntityGetParent(doc, handle);

  if (!parent || !docEntityHandleToEntityRef(doc, parent)) {
    return false;
  }

  const mutableChildTypes = docRefEligibleEntityTypeToMutableDetachedChildTypes[parent.type];

  if (!mutableChildTypes?.length) {
    return false;
  }

  return mutableChildTypes.includes(handle.type);
}

export function docEntityIsMutableDocRefEligibleChild(doc: Doc, handle: DocEntityHandle): boolean {
  const parent = docEntityGetParent(doc, handle);

  if (!parent) {
    return false;
  }

  const mutableChildTypes = docRefEligibleEntityTypeToMutableChildTypes[parent.type];

  if (!mutableChildTypes?.length) {
    return false;
  }

  return mutableChildTypes.includes(handle.type);
}

type MutableOp = "DUPLICATE" | "DELETE";

export function docEntityIsReadonly(doc: Doc, handle: DocEntityHandle, operation?: MutableOp): boolean {
  let readonly = false;

  if (docEntityOrParentIsLocked(doc, handle)) {
    return true;
  }

  const predicates: ((doc: Doc, handle: DocEntityHandle, operation?: MutableOp) => boolean)[] = [
    docEntityIsReadonlyEntityRef,
    docEntityIsReadonlyTypeSpecific,
  ];

  for (const predicate of predicates) {
    if (predicate(doc, handle, operation)) {
      readonly = true;
      break;
    }
  }

  return readonly;
}

function docEntityIsReadonlyTypeSpecific(doc: Doc, handle: DocEntityHandle, operation?: MutableOp): boolean {
  switch (handle.type) {
    case "GROUP": {
      const group = docGetGroup(doc, handle);

      if (!group) {
        console.error("Group not found", handle);
        return true;
      }

      if (!!group.isContentMutableBuiltIn) {
        return true;
      }

      // Built-in catalog groups may be isDeletable; still treat them as read-only except for Delete (SDO-5036).
      if (operation === "DELETE") {
        return !group.isDeletable && !!group.isBuiltIn;
      }

      return !!group.isBuiltIn;
    }
    case "LABEL": {
      return isBuiltInLabel(handle.id);
    }
    case "FRAMING_COMPONENT": {
      return isBuiltInFramingComponent(handle.id);
    }
    case "HEADER_ASSEMBLY": {
      return isBuiltInHeaderAssembly(handle.id);
    }
    case "FOUNDATION_ASSEMBLY": {
      return isBuiltInFoundationAssembly(handle.id);
    }
    case "FOUNDATION_COMPONENT": {
      return isBuiltInFoundationComponent(handle.id);
    }
    case "SLAB_COMPONENT": {
      return isBuiltInSlabFloorComponent(handle.id);
    }
    case "SHEATHING_COMPONENT": {
      return isBuiltInSheathingComponent(handle.id);
    }
    case "MASONRY_COMPONENT": {
      return isBuiltInMasonryComponent(handle.id);
    }
    case "CIP_COMPONENT": {
      return isBuiltInCipComponent(handle.id);
    }
    case "FRAME_FLOOR_ASSEMBLY": {
      return isBuiltInFrameFloorAssembly(handle.id);
    }
    case "SLAB_FLOOR_ASSEMBLY": {
      return isBuiltInSlabFloorAssembly(handle.id);
    }
    case "PROFILE": {
      return isSystemDefaultProfile(handle.id);
    }
    default: {
      return false;
    }
  }
}

/**
 * This is not the 'one-stop-shop' for determining if an entity is readonly, it rather
 * serves as the initial buffer to determine if the entity has a doc ref'd parent (or group).
 * If it does, it should be considered readonly. If not, then more granular checks are probably needed.
 */
export function docEntityIsReadonlyEntityRef(doc: Doc, handle: DocEntityHandle, operation?: MutableOp): boolean {
  const entityGroup = docEntityGetGroup(doc, handle);

  // If the entity is in a doc ref'd group, it is not able to be deleted
  if (entityGroup && operation === "DELETE") {
    return !!docEntityHandleToEntityRef(doc, entityGroup);
  }

  const entityRoomLayout = docEntityGetRoomLayout(doc, handle);

  // If the entity is a member of a doc ref'd room layout, it is readonly
  if (entityRoomLayout || (handle.type === "ROOM_LAYOUT" && operation === "DUPLICATE")) {
    return !!docEntityHandleToEntityRef(doc, entityRoomLayout ?? handle);
  }

  if (handle.type === "ROOM" && docEntityHandleToEntityRef(doc, handle)) {
    return true;
  }

  if (docEntityIsMutableDocRefEligibleChild(doc, handle)) {
    return false;
  }

  if (docEntityIsMutableDetachedChild(doc, handle)) {
    return false;
  }

  const parent = docEntityGetParent(doc, handle);

  if (parent && operation !== "DUPLICATE") {
    return !!docEntityHandleToEntityRef(doc, parent);
  }

  return false;
}
